import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errorText, TOKEN_KEY } from "@/lib/api";
import { BookingsTab } from "@/components/owner/BookingsTab";
import { MessagesTab } from "@/components/owner/MessagesTab";
import { ScheduleTab } from "@/components/owner/ScheduleTab";
import { VipTab } from "@/components/owner/VipTab";
import { PhotosTab } from "@/components/owner/PhotosTab";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
    } catch (e2) {
      setErr(errorText(e2, "Could not log in."));
    } finally { setBusy(false); }
  };
  return (
    <div className="owner-login-wrap">
      <motion.form onSubmit={submit} className="owner-card owner-login" initial={{ y: 24, opacity: 0.3 }}
        animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7 }} data-testid="owner-login-form">
        <Crown className="owner-crown" strokeWidth={1.3} />
        <h1>The Owner's Suite</h1>
        <p>Private. Only Smiless gets past this velvet rope.</p>
        <label className="st-label">Email<input className="st-control" type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)} autoComplete="username" data-testid="owner-email-input" /></label>
        <label className="st-label">Password<input className="st-control" type="password" required value={password}
          onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="owner-password-input" /></label>
        {err ? <p className="form-err" data-testid="owner-login-error">{err}</p> : null}
        <button className="cta w-full" disabled={busy} data-testid="owner-login-submit">{busy ? "Opening…" : "Enter the Suite"}</button>
        <a href="/" className="owner-link">← Back to the House</a>
      </motion.form>
    </div>
  );
};

export default function OwnerPage() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setUser(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(false); });
  }, []);
  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setUser(false); }, []);

  if (user === null) return <div className="owner-shell" />;
  if (!user) return <div className="owner-shell"><Login onLogin={setUser} /></div>;
  return (
    <div className="owner-shell" data-testid="owner-dashboard">
      <header className="owner-head">
        <a href="/" className="owner-brand"><Crown strokeWidth={1.4} /> LocdbySmiless <span>Owner's Suite</span></a>
        <button type="button" className="cta ghost sm" onClick={logout} data-testid="owner-logout-button"><LogOut size={16} /> Log out</button>
      </header>
      <main className="owner-main">
        <h1 className="owner-title">Hey Smiless, <em>here's your house.</em></h1>
        <Tabs defaultValue="bookings">
          <TabsList className="owner-tabs">
            <TabsTrigger value="bookings" data-testid="owner-tab-bookings">Booking requests</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="owner-tab-schedule">Closed days</TabsTrigger>
            <TabsTrigger value="messages" data-testid="owner-tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="photos" data-testid="owner-tab-photos">Website photos</TabsTrigger>
            <TabsTrigger value="vip" data-testid="owner-tab-vip">VIP Lounge</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings"><BookingsTab onAuthFail={logout} /></TabsContent>
          <TabsContent value="schedule"><ScheduleTab /></TabsContent>
          <TabsContent value="messages"><MessagesTab /></TabsContent>
          <TabsContent value="photos"><PhotosTab /></TabsContent>
          <TabsContent value="vip"><VipTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
